// src/components/EmbeddedSlideshow.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';

// --- BRAND PALETTE ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F',
    success: '#81C784'
};



const stripMarkdownFormatting = (value = '') => String(value)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1');

const escapeRegex = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HoverableKeywordText = ({ text = '', translationPairs = [], translationLabel = 'Translation' }) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [tooltipRect, setTooltipRect] = useState(null);
    const spanRefs = useRef({});
    const sanitizedText = stripMarkdownFormatting(text);

    if (!sanitizedText) return null;

    const filteredPairs = (translationPairs || [])
        .filter(pair => pair?.term && pair?.translation)
        .sort((a, b) => b.term.length - a.term.length);

    if (filteredPairs.length === 0) return <ReactMarkdown components={{ p: 'span' }}>{sanitizedText}</ReactMarkdown>;

    const combinedRegex = new RegExp(`(${filteredPairs.map(pair => escapeRegex(pair.term)).join('|')})`, 'gi');
    const parts = String(sanitizedText).split(combinedRegex);
    const normalizedTranslationLabel = `${String(translationLabel || 'Translation').trim().replace(/:$/, '')}:`;

    const handleMouseEnter = (index) => {
        const el = spanRefs.current[index];
        if (el) setTooltipRect(el.getBoundingClientRect());
        setHoveredIndex(index);
    };
    const handleMouseLeave = () => { setHoveredIndex(null); setTooltipRect(null); };

    const hoveredPair = hoveredIndex !== null
        ? filteredPairs.find(p => p.term.toLowerCase() === (parts[hoveredIndex] || '').toLowerCase())
        : null;

    const tooltip = (hoveredIndex !== null && tooltipRect && hoveredPair)
        ? ReactDOM.createPortal(
            <span style={{
                position: 'fixed',
                left: `${tooltipRect.left + tooltipRect.width / 2}px`,
                top: `${tooltipRect.bottom + 8}px`,
                transform: 'translateX(-50%)',
                background: '#fff',
                border: `2px solid ${BRAND.teal}`,
                borderRadius: '8px',
                padding: '8px 10px',
                boxShadow: '3px 3px 0px rgba(45,64,66,0.12)',
                whiteSpace: 'normal',
                minWidth: '200px',
                maxWidth: '320px',
                zIndex: 9999,
                fontSize: '0.85em',
                fontWeight: '700',
                color: BRAND.charcoal,
                textAlign: 'left',
                pointerEvents: 'none',
            }}>
                <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                    {normalizedTranslationLabel}
                </div>
                {stripMarkdownFormatting(hoveredPair.translation)}
            </span>,
            document.body
        )
        : null;

    return (
        <>
            {parts.map((part, index) => {
                const pair = filteredPairs.find(p => p.term.toLowerCase() === part.toLowerCase());
                if (!pair) return <span key={`text-${index}`}>{part}</span>;
                return (
                    <span
                        key={`term-${index}`}
                        ref={el => { spanRefs.current[index] = el; }}
                        style={{ display: 'inline-block', fontWeight: '700', color: BRAND.charcoal, cursor: 'help' }}
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={handleMouseLeave}
                    >
                        {part}
                    </span>
                );
            })}
            {tooltip}
        </>
    );
};
const EmbeddedSlideshow = ({
    slideshowData,
    onSlideshowComplete,
    audioEnabled = true,
    initialPlaybackSpeed = 1,
    enableHoverTranslations = false,
    translationLabel = 'Translation',
    translationPairs = [],
    bgTheme = null
}) => {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState([0.5, 0.75, 1].includes(Number(initialPlaybackSpeed)) ? Number(initialPlaybackSpeed) : 1); 
    
    const speedRef = useRef(playbackSpeed);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

    const getFullUrl = useCallback((path) => {
        if (!path) return '';
        return path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;
    }, [API_BASE_URL]);

    useEffect(() => {
        const audioElement = audioRef.current;
        const stop = () => setIsPlaying(false);
        const start = () => setIsPlaying(true);
        
        if (audioElement) {
            audioElement.addEventListener('ended', stop);
            audioElement.addEventListener('pause', stop);
            audioElement.addEventListener('play', start);
            return () => {
                audioElement.removeEventListener('ended', stop);
                audioElement.removeEventListener('pause', stop);
                audioElement.removeEventListener('play', start);
            };
        }
    }, []);

    useEffect(() => {
        speedRef.current = playbackSpeed;
        if (audioRef.current) {
            audioRef.current.playbackRate = playbackSpeed;
        }
    }, [playbackSpeed]);

    useEffect(() => {
        const normalized = [0.5, 0.75, 1].includes(Number(initialPlaybackSpeed)) ? Number(initialPlaybackSpeed) : 1;
        setPlaybackSpeed(normalized);
    }, [initialPlaybackSpeed]);

    const currentSlide = slideshowData?.slides?.[currentSlideIndex];
    const captionText = currentSlide?.text || currentSlide?.narration || currentSlide?.originalText || '';
    const hoverTranslationPairs = Array.isArray(translationPairs) ? translationPairs : [];
    const lowerCaptionText = String(stripMarkdownFormatting(captionText || '')).toLowerCase();
    const captionKeywordPairs = hoverTranslationPairs.filter((pair) => {
        const term = String(pair?.term || '').trim().toLowerCase();
        if (!term) return false;
        if (term === lowerCaptionText) return false; // never hover-translate a full caption block
        return lowerCaptionText.includes(term);
    });
    const showHoverTranslation = Boolean(enableHoverTranslations && captionKeywordPairs.length > 0);

    useEffect(() => {
        if (currentSlide?.audioUrl && audioRef.current) {
            audioRef.current.src = getFullUrl(currentSlide.audioUrl);
            audioRef.current.playbackRate = speedRef.current; 
            audioRef.current.load();
            setIsPlaying(false);
        }
    }, [currentSlide, API_BASE_URL, getFullUrl]);

    if (!slideshowData?.slides?.length) {
        return (
            <div style={styles.container}>
                <p style={{fontFamily: 'Nunito', fontWeight: '700', color: BRAND.charcoal}}>No slideshow data found.</p>
                <button onClick={onSlideshowComplete} style={styles.primaryButton}>Continue</button>
            </div>
        );
    }

    const { slides } = slideshowData;
    const isLastSlide = currentSlideIndex === slides.length - 1;

    const handleNext = () => {
        if (audioRef.current) audioRef.current.pause();
        if (!isLastSlide) setCurrentSlideIndex(prev => prev + 1);
        else onSlideshowComplete();
    };

    const handlePrev = () => {
        if (audioRef.current) audioRef.current.pause();
        if (currentSlideIndex > 0) setCurrentSlideIndex(prev => prev - 1);
    };

    const togglePlayPause = () => {
        if (!audioRef.current || !currentSlide?.audioUrl) return;
        if (isPlaying) audioRef.current.pause();
        else audioRef.current.play().catch(e => console.error(e));
    };

    const cycleSpeed = () => {
        const speeds = [0.5, 0.75, 1];
        const nextIdx = (speeds.indexOf(playbackSpeed) + 1) % speeds.length;
        setPlaybackSpeed(speeds[nextIdx]);
    };

    return (
        <div style={styles.container}>
            {/* +++ REMOVED: Header Row with Slide Counter +++ */}
            
            <div style={styles.slideContent}>
                <div style={styles.imageWrapper}>
                    <img
                        key={currentSlide.imageUrl}
                        src={getFullUrl(currentSlide.imageUrl)}
                        alt={`Slide ${currentSlideIndex + 1}`}
                        style={styles.image}
                        onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/600x400/FDF6EC/2D4042?text=Image+Not+Found"; }}
                    />
                </div>
                
                <div style={{ ...styles.narrationBox, background: bgTheme || BRAND.cream }}>
                    {showHoverTranslation ? (
                        <HoverableKeywordText
                            text={captionText}
                            translationPairs={captionKeywordPairs}
                            translationLabel={translationLabel}
                        />
                    ) : (
                        <ReactMarkdown components={{ p: 'span' }}>
                            {captionText}
                        </ReactMarkdown>
                    )}
                </div>
                <audio ref={audioRef} key={currentSlide.audioUrl} style={{ display: 'none' }} />
            </div>

            <div style={styles.navigation}>
                <button 
                    onClick={handlePrev} 
                    disabled={currentSlideIndex === 0} 
                    style={currentSlideIndex === 0 ? styles.disabledButton : styles.secondaryButton}
                >
                    Previous
                </button>
                
                {audioEnabled && (
                    <div style={{display:'flex', gap:'8px'}}>
                        <button 
                            onClick={togglePlayPause} 
                            disabled={!currentSlide.audioUrl} 
                            style={!currentSlide.audioUrl ? styles.disabledButton : styles.actionButton}
                        >
                            {isPlaying ? 'Pause' : 'Play Audio'}
                        </button>
                        <button 
                            onClick={cycleSpeed} 
                            disabled={!currentSlide.audioUrl} 
                            style={!currentSlide.audioUrl ? styles.disabledButton : styles.smallButton}
                        >
                            {playbackSpeed}x
                        </button>
                    </div>
                )}

                <button 
                    onClick={handleNext} 
                    style={isLastSlide ? styles.finishButton : styles.primaryButton}
                >
                    {isLastSlide ? 'Finish' : 'Next'}
                </button>
            </div>
        </div>
    );
};

// --- STYLES ---
const baseButton = {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: '800',
    border: `2px solid ${BRAND.charcoal}`,
    borderRadius: '8px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '0.95em',
    transition: 'all 0.1s ease',
    borderBottom: `4px solid ${BRAND.charcoal}`,
    outline: 'none'
};

const styles = {
    container: { 
        padding: '25px', 
        border: `2px solid ${BRAND.charcoal}`, 
        borderRadius: '16px', 
        background: 'white',
        boxShadow: '4px 4px 0px rgba(45, 64, 66, 0.1)'
    },
    // Removed headerRow and slideCounter styles
    slideContent: { 
        minHeight: '300px', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: '20px' 
    },
    imageWrapper: {
        width: '100%',
        maxWidth: '600px',
        border: `2px solid ${BRAND.charcoal}`,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '4px 4px 0px rgba(45, 64, 66, 0.1)',
        background: BRAND.grey
    },
    image: { 
        width: '100%', 
        height: 'auto', 
        display: 'block',
        maxHeight: '350px',
        objectFit: 'contain'
    },
    narrationBox: { 
        minHeight: '3em', 
        background: BRAND.cream, 
        padding: '20px', 
        borderRadius: '12px', 
        width: '100%', 
        textAlign: 'center', 
        fontSize: '1.1em', 
        lineHeight: '1.6', 
        color: BRAND.charcoal,
        fontFamily: 'Inter, sans-serif',
        border: `1px solid ${BRAND.warning}`,
        boxSizing: 'border-box'
    },
    navigation: { 
        marginTop: '30px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap'
    },
    
    // Button Variants
    primaryButton: {
        ...baseButton,
        backgroundColor: BRAND.teal,
        color: 'white',
    },
    secondaryButton: {
        ...baseButton,
        backgroundColor: 'white',
        color: BRAND.charcoal,
    },
    actionButton: {
        ...baseButton,
        backgroundColor: BRAND.warning,
        color: BRAND.charcoal,
        minWidth: '110px'
    },
    smallButton: {
        ...baseButton,
        padding: '10px 14px',
        backgroundColor: '#F0F2F5',
        color: BRAND.charcoal
    },
    finishButton: {
        ...baseButton,
        backgroundColor: BRAND.coral,
        color: 'white',
    },
    disabledButton: {
        ...baseButton,
        backgroundColor: '#E0E0E0',
        color: '#999',
        borderColor: '#CCC',
        borderBottom: '2px solid #CCC',
        cursor: 'not-allowed',
        transform: 'none'
    }
};

export default EmbeddedSlideshow;
